import React, { useState } from 'react';
import { Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Paper, IconButton, TableSortLabel } from '@mui/material';
import { RenderParticipantName } from "./Elements";
import Trend from '../utils/Trend';
import { useParticipantRanks } from '../utils/useParticipantRanks';

export interface ProgressTableProps {
    data: any[]; // ParticipantProgress[];
    onParticipantClick: (participantId: number, name: string) => void;
}

const ProgressTable: React.FC<ProgressTableProps> = ({ data, onParticipantClick }) => {
    const [sortConfig, setSortConfig] = useState<{ key: string, direction: 'asc' | 'desc' } | null>(null);
    const { ranksByParticipantId } = useParticipantRanks();

    const sortedData = React.useMemo(() => {
        if (sortConfig !== null) {
            return [...data].sort((a, b) => {
                const aValue = a[sortConfig.key];
                const bValue = b[sortConfig.key];

                if (aValue === null || aValue === undefined) return 1;
                if (bValue === null || bValue === undefined) return -1;

                if (sortConfig.key === 'total_entries') {
                    const aNum = Number(aValue);
                    const bNum = Number(bValue);
                    return sortConfig.direction === 'asc' ? aNum - bNum : bNum - aNum;
                }

                if (aValue < bValue) {
                    return sortConfig.direction === 'asc' ? -1 : 1;
                }
                if (aValue > bValue) {
                    return sortConfig.direction === 'asc' ? 1 : -1;
                }
                return 0;
            });
        }
        return data;
    }, [data, sortConfig]);

    const requestSort = (key: string) => {
        let direction: 'asc' | 'desc' = 'asc';
        if (sortConfig && sortConfig.key === key && sortConfig.direction === 'asc') {
            direction = 'desc';
        }
        setSortConfig({ key, direction });
    };

    return (
        <TableContainer component={Paper}>
            <Table>
                <TableHead>
                    <TableRow>
                        <TableCell />
                        <TableCell>
                            <TableSortLabel
                                active={sortConfig?.key === 'participant_name'}
                                direction={sortConfig?.direction}
                                onClick={() => requestSort('participant_name')}
                            >
                                Участник
                            </TableSortLabel>
                        </TableCell>
                        <TableCell>
                            <TableSortLabel
                                active={sortConfig?.key === 'total_progress'}
                                direction={sortConfig?.direction}
                                onClick={() => requestSort('total_progress')}
                            >
                                ∑ прогресс (%)
                            </TableSortLabel>
                        </TableCell>
                        <TableCell>
                            <TableSortLabel
                                active={sortConfig?.key === 'max_progress'}
                                direction={sortConfig?.direction}
                                onClick={() => requestSort('max_progress')}
                            >
                                Макс. прогресс (%)
                            </TableSortLabel>
                        </TableCell>
                        <TableCell>
                            <TableSortLabel
                                active={sortConfig?.key === 'total_entries'}
                                direction={sortConfig?.direction}
                                onClick={() => requestSort('total_entries')}
                            >
                                Записей
                            </TableSortLabel>
                        </TableCell>
                        <TableCell>
                            <TableSortLabel
                                active={sortConfig?.key === 'last_activity'}
                                direction={sortConfig?.direction}
                                onClick={() => requestSort('last_activity')}
                            >
                                Последняя запись
                            </TableSortLabel>
                        </TableCell>
                    </TableRow>
                </TableHead>
                <TableBody>
                    {sortedData.map((row) => (
                        <TableRow
                            key={row.participant_id}
                            sx={{
                                cursor: 'pointer',
                                '&:hover': { backgroundColor: 'rgba(0, 0, 0, 0.04)' },
                            }}
                            onClick={() => onParticipantClick(row.participant_id, row.participant_name)}
                        >
                            <TableCell padding="checkbox" onClick={(e) => e.stopPropagation()}>
                                <IconButton
                                    onClick={() => onParticipantClick(row.participant_id, row.participant_name)}
                                    size="small"
                                    aria-label="Подробнее"
                                >
                                    <Trend trend={row.progress_trend} />
                                </IconButton>
                            </TableCell>
                            <TableCell>
                                {RenderParticipantName(
                                    row.participant_id,
                                    row.participant_name,
                                    row.profile_picture,
                                    true,
                                    ranksByParticipantId.get(row.participant_id) ?? []
                                )}
                            </TableCell>
                            <TableCell>{row.total_progress.toFixed(0)}</TableCell>
                            <TableCell>{row.max_progress.toFixed(0)}</TableCell>
                            <TableCell>{row.total_entries}</TableCell>
                            <TableCell>{new Date(row.last_activity).toLocaleString()}</TableCell>
                        </TableRow>
                    ))}
                </TableBody>
            </Table>
        </TableContainer>
    );
};

export default ProgressTable;